import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { FormResponseValue } from "@tellescope/types-models";
import { useCallback, useEffect, useMemo } from "react";
import {
  buildTreeFromFields,
  collectIdsToDropOnBack,
  countFormPages,
  countRemainingFormPages,
  isValueEmpty,
  resolveInitialNavState,
  resolveNextDisplayableField,
  subFieldIdsOf,
} from "../../utils";
import type { FormFieldNode, NextFieldLogicOptions } from "../../utils/types";
import type { LocalTellescopeFormResponseMap } from "../use-tellescope-form-core";
import type { FormShallowStoreHook } from "../../store";
import { useFormNavigationState } from "./use-form-navigation-state";
import { useTrackFieldView } from "./use-track-field-view";
import { useTrackFieldProgress } from "../use-form-analytics";

const hasNoActiveField = (field: FormFieldNode | undefined) => !field;

const percentageForPage = (pageIndex: number, totalPages: number) => ((pageIndex + 1) / totalPages) * 100;

export interface UseFormNavigationArgs {
  fieldsArr: TellescopeModelFromName<"form_fields">[] | undefined;
  fieldsMap: Record<string, TellescopeModelFromName<"form_fields">>;
  subFieldIds: Set<string>;
  currentResponses: LocalTellescopeFormResponseMap;
  responsesMap: Record<string, FormResponseValue>;
  logicOptions: NextFieldLogicOptions;
  existingResponses: FormResponseValue[] | undefined;
  startingFieldId: string | undefined;
  onBeforeAdvance: (activeField: FormFieldNode) => void;
  responseId: string | undefined;
  responseAccessCode: string | undefined;
  useFormShallowStore: FormShallowStoreHook;
}

export const useFormNavigation = ({
  fieldsArr,
  fieldsMap,
  subFieldIds,
  currentResponses,
  responsesMap,
  logicOptions,
  existingResponses,
  startingFieldId,
  onBeforeAdvance,
  responseId,
  responseAccessCode,
  useFormShallowStore,
}: UseFormNavigationArgs) => {
  const fieldTree = useMemo(() => {
    if (!fieldsArr?.length) return undefined;
    return buildTreeFromFields(fieldsArr);
  }, [fieldsArr]);

  const hasNoRootField = !!fieldsArr?.length && !fieldTree;

  const existingResponseFieldIds = useMemo(
    () => new Set(existingResponses?.map((r) => r.fieldId) ?? []),
    [existingResponses]
  );

  const initialNavState = useMemo(() => {
    if (!fieldTree) return undefined;

    return resolveInitialNavState({
      fieldTree,
      fieldsMap,
      subFieldIds,
      answeredFieldIds: new Set(Object.keys(responsesMap)),
      existingResponseFieldIds,
      startingFieldId,
      walkContext: {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses).filter((r) => !isValueEmpty(r.answer?.value)),
        logicOptions,
      },
    });
  }, [
    fieldTree,
    fieldsMap,
    subFieldIds,
    currentResponses,
    logicOptions,
    responsesMap,
    startingFieldId,
    existingResponseFieldIds,
  ]);

  const {
    activeField: storedActiveField,
    setActiveField: setStoredActiveField,
    includedFieldIds: storedIncludedFieldIds,
    setIncludedFieldIds: setStoredIncludedFieldIds,
    prevFieldStack: storedPrevFieldStack,
    setPrevFieldStack: setStoredPrevFieldStack,
    navigatedProgress,
    setNavigatedProgress,
  } = useFormShallowStore(
    "activeField",
    "setActiveField",
    "includedFieldIds",
    "setIncludedFieldIds",
    "prevFieldStack",
    "setPrevFieldStack",
    "navigatedProgress",
    "setNavigatedProgress"
  );

  const [resolvedActiveField, setActiveField] = useFormNavigationState<FormFieldNode | undefined>({
    stored: storedActiveField,
    setStored: setStoredActiveField,
    fallbackValue: initialNavState?.activeField,
    isEmpty: hasNoActiveField,
  });

  const trackFieldView = useTrackFieldView({ responseId, accessCode: responseAccessCode });
  const trackFieldProgress = useTrackFieldProgress();

  // Navigation views are interaction-caused and reported by the handlers below; the opening field is
  // display-caused (nobody navigated to it), which is the one shape react.dev/learn/you-might-not-need-an-effect
  // keeps in an effect. Repeat runs are absorbed by the trackers' own per-field dedupe.
  const openingField = storedActiveField === undefined ? initialNavState?.activeField : undefined;
  useEffect(() => {
    if (!openingField) return;
    trackFieldView(openingField.value.id);
    trackFieldProgress(openingField);
  }, [openingField, trackFieldView, trackFieldProgress]);

  const includedFieldIdsFallback = useMemo(
    () => initialNavState?.included ?? new Set<string>(),
    [initialNavState?.included]
  );
  const [includedFieldIds, setIncludedFieldIds] = useFormNavigationState<Set<string>>({
    stored: storedIncludedFieldIds,
    setStored: setStoredIncludedFieldIds,
    fallbackValue: includedFieldIdsFallback,
  });

  const prevFieldStackFallback = useMemo(() => initialNavState?.prevStack ?? [], [initialNavState?.prevStack]);
  // An empty stack is a real state — the user navigated back to the first page — so it must
  // stay distinguishable from "not yet seeded". Treating it as empty would resolve back to
  // the resume stack and report a stale currentPageIndex.
  const [resolvedPrevStack, setPrevFieldStack] = useFormNavigationState<FormFieldNode[]>({
    stored: storedPrevFieldStack,
    setStored: setStoredPrevFieldStack,
    fallbackValue: prevFieldStackFallback,
  });

  const currentPageIndex = resolvedPrevStack.length;

  const totalPages = useMemo(() => {
    if (!fieldTree) return 1;

    return countFormPages(
      fieldTree,
      subFieldIds,
      {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses),
        logicOptions,
      },
      fieldsMap
    );
  }, [fieldTree, currentResponses, logicOptions, subFieldIds, fieldsMap]);

  const progress = navigatedProgress ?? percentageForPage(currentPageIndex, totalPages);

  const { nextField, skippedHiddenFieldIds } = useMemo((): {
    nextField: FormFieldNode | undefined;
    skippedHiddenFieldIds: string[];
  } => {
    if (!resolvedActiveField) return { nextField: undefined, skippedHiddenFieldIds: [] };

    return resolveNextDisplayableField({
      activeField: resolvedActiveField,
      fieldsMap,
      subFieldIds,
      walkContext: {
        responsesByFieldId: currentResponses,
        allResponses: Object.values(currentResponses),
        logicOptions,
      },
    });
  }, [resolvedActiveField, currentResponses, logicOptions, subFieldIds, fieldsMap]);

  const goToNextFieldInternal = useCallback(() => {
    if (!resolvedActiveField || !nextField) return;

    onBeforeAdvance(resolvedActiveField);

    const subFieldIdsToInclude = subFieldIdsOf(resolvedActiveField.value);

    setPrevFieldStack((prev) => [...prev, resolvedActiveField]);
    setIncludedFieldIds((prev) => {
      const next = new Set(prev);
      subFieldIdsToInclude.forEach((id) => next.add(id));
      skippedHiddenFieldIds.forEach((id) => next.add(id));
      next.add(nextField.value.id);
      return next;
    });
    setActiveField(nextField);
    trackFieldView(nextField.value.id);
    trackFieldProgress(nextField, resolvedActiveField);
    setNavigatedProgress(percentageForPage(currentPageIndex + 1, totalPages));
  }, [
    resolvedActiveField,
    nextField,
    onBeforeAdvance,
    setPrevFieldStack,
    setIncludedFieldIds,
    setActiveField,
    trackFieldView,
    trackFieldProgress,
    setNavigatedProgress,
    currentPageIndex,
    totalPages,
    skippedHiddenFieldIds,
  ]);

  const goToPreviousField = useCallback(() => {
    const stack = resolvedPrevStack;
    if (stack.length === 0) return;
    if (resolvedActiveField?.value?.options?.disableGoBack) return;

    const previous = stack[stack.length - 1];

    const idsToDrop = collectIdsToDropOnBack(resolvedActiveField, previous, {
      responsesByFieldId: currentResponses,
      allResponses: Object.values(currentResponses),
      logicOptions,
    });

    setIncludedFieldIds((prev) => new Set(Array.from(prev).filter((id) => !idsToDrop.has(id))));

    if (previous) {
      setPrevFieldStack(stack.slice(0, -1));
      setActiveField(previous);
      trackFieldView(previous.value.id);
      // Reported with the field being left as `previousTitle`, matching legacy, which read whatever
      // sat on top of its prev-field stack at the moment the event fired.
      trackFieldProgress(previous, resolvedActiveField);
      setNavigatedProgress(percentageForPage(currentPageIndex - 1, totalPages));
    }
  }, [
    resolvedPrevStack,
    resolvedActiveField,
    currentResponses,
    logicOptions,
    setIncludedFieldIds,
    setPrevFieldStack,
    setActiveField,
    trackFieldView,
    trackFieldProgress,
    setNavigatedProgress,
    currentPageIndex,
    totalPages,
  ]);

  const isPreviousDisabled = useMemo(() => {
    if (resolvedPrevStack.length === 0) return true;
    if (resolvedActiveField?.value?.options?.disableGoBack) return true;
    return false;
  }, [resolvedPrevStack.length, resolvedActiveField?.value?.options?.disableGoBack]);

  const showSubmit = useMemo(() => {
    if (!resolvedActiveField) return false;
    return resolvedActiveField.children.length === 0 || !nextField;
  }, [resolvedActiveField, nextField]);

  const getNumberOfRemainingPages = useCallback(() => {
    if (!resolvedActiveField) return 0;
    return countRemainingFormPages(resolvedActiveField.value, fieldsArr ?? [], subFieldIds);
  }, [resolvedActiveField, fieldsArr, subFieldIds]);

  return {
    resolvedActiveField,
    hasNoRootField,
    includedFieldIds,
    resolvedPrevStack,
    currentPageIndex,
    totalPages,
    progress,
    nextField,
    goToNextFieldInternal,
    goToPreviousField,
    isPreviousDisabled,
    showSubmit,
    getNumberOfRemainingPages,
  };
};
